//1
'use client';

import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import RemindersBadge from '@/components/RemindersBadge';
import { Search, Check, Clock, UserCircle2, ChevronRight, ChevronDown, ChevronUp, RefreshCcw, X, Calendar, LogOut, User, UserCog, Pencil, Bell, BellOff, Ban, FileText, Eye, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ProtectedComponent } from "./AuthProvider";
import SearchTypeSelector from '@/components/SearchTypeSelector';
import { useDashboard } from '../hooks/useDashboard';
import UserAccountMenu from './UserAccountMenu';
import { trackDashboardLoad } from '../utils/telemetry';
import ReassignmentModal from './ReassignmentModal';
import VoidEnvelopeModal from './VoidEnvelopeModal';
import RemoveDocumentModal from './RemoveDocumentModal';

// Environment debug logging (only on client-side)
if (typeof window !== 'undefined') {
  console.log('[Dashboard] Environment check:', {
    NODE_ENV: process.env.NODE_ENV,
    DOCUSIGN_ENV: process.env.NEXT_PUBLIC_DOCUSIGN_ENVIRONMENT,
    API_URL: process.env.NEXT_PUBLIC_API_URL,
    location: window.location.origin
  });
}

// Common helper functions for envelope status display
const getSignerStatus = (recipient) => {
  switch (recipient.status?.toLowerCase()) {
    case 'completed':
      return 'bg-[#008455]'; // Dark green
    case 'sent':
    case 'delivered':
      return 'bg-[#98FF98]'; // Light green
    case 'autoresponded': // DocuSign hard bounce - the invite email was rejected
    case 'declined':
      return 'bg-red-500';
    default:
      return 'bg-gray-200'; // Default gray
  }
};

const getSignerIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return <Check className="w-5 h-5 text-white" />;
    case 'sent':
    case 'delivered':
      return <Clock className="w-5 h-5 text-black" />;
    case 'autoresponded':
    case 'declined':
      return <AlertCircle className="w-5 h-5 text-white" />;
    default:
      return <UserCircle2 className="w-5 h-5 text-gray-400" />;
  }
};

const getProgressBarStyle = (recipient, isSequential, isNext) => {
  const status = recipient.status?.toLowerCase();

  if (status === 'completed') {
    return 'bg-[#008455]'; // Dark green for completed
  }

  if (isSequential && isNext) {
    return 'bg-gradient-to-r from-[#008455] via-[#98FF98] to-[#98FF98]'; // Enhanced gradient for next in sequence
  }

  if (status === 'sent' || status === 'delivered') {
    return 'bg-[#98FF98]'; // Light green for pending
  }

  return 'bg-gray-200'; // Default gray
};

const getStatusDisplay = (envelope) => {
  if (envelope.status?.toLowerCase() !== 'sent' && envelope.status?.toLowerCase() !== 'delivered') {
    return envelope.status;
  }

  // A bounced invite otherwise falls through to the bare envelope status ('sent')
  const bounced = envelope.recipients?.filter(r => r.status?.toLowerCase() === 'autoresponded') || [];
  if (bounced.length === 1) {
    return `Email failed to deliver to ${bounced[0].name || bounced[0].email?.split('@')[0] || 'recipient'}`;
  }
  if (bounced.length > 1) {
    return `Email failed to deliver to ${bounced.length} signers`;
  }

  const unsignedRecipients = envelope.recipients?.filter(r =>
    r.status === 'sent' ||
    r.status === 'created' ||
    r.status === 'delivered'
  ) || [];

  // Check if it's a linear/sequential signing
  const isSequential = envelope.recipients?.some(r => r.routingOrder > 1);

  if (isSequential) {
    // Find the next person who needs to sign (lowest routing order among unsigned who haven't completed)
    const completedRoutingOrders = new Set(
      envelope.recipients
        .filter(r => r.status === 'completed')
        .map(r => r.routingOrder)
    );

    const nextSigner = unsignedRecipients
      .filter(r => !completedRoutingOrders.has(r.routingOrder))
      .reduce((lowest, current) =>
        !lowest || (current.routingOrder < lowest.routingOrder) ? current : lowest
      , null);

    if (nextSigner) {
      return `Waiting for ${nextSigner.name}`;
    }
  } else {
    // Non-sequential signing
    if (unsignedRecipients.length === 1) {
      return `Waiting for ${unsignedRecipients[0].name}`;
    } else if (unsignedRecipients.length > 1) {
      return 'Waiting for multiple signers';
    }
  }

  return envelope.status;
};

// Unified Progress Component - Used for showing all signing progress
const SequentialProgress = ({ recipients, formatDate, isSequential = false }) => {
	const sortedRecipients = [...recipients].sort((a, b) => {
    const orderA = a.routingOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.routingOrder ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });
  const waitingSignerIndex = recipients.findIndex(r => r.status === 'sent');

  const getSegmentStyle = (recipientIndex) => {
  const recipient = sortedRecipients[recipientIndex];
  const status = recipient.status?.toLowerCase();

  // Completed - dark green segment
  if (status === 'completed') {
    return {
      height: '0.75rem',
      background: '#008455',
      marginTop: '0'
    };
  }

  // Sent or Delivered - gradient segment
  if (status === 'sent' || status === 'delivered') {
    return {
      height: '0.75rem',
      background: 'linear-gradient(to right, #008455, #98FF98)',
      marginTop: '0'
    };
  }

  // Created - thin grey segment
  return {
    height: '0.25rem',
    background: '#767779',
    marginTop: '0.25rem'
  };
};

// Add the new StatusBar component here
const StatusBar = ({ recipient, showArrow, idx, totalRecipients }) => {
  return (
    <div className="flex-1 relative">
      <div className="relative">
        <div className="h-3 bg-[#767779] rounded-full">
          <div
            className="h-3 transition-all duration-500 rounded-full"
            style={{
              width: '100%',
              background: recipient.status === 'completed'
                ? '#008455'
                : 'linear-gradient(to right, #008455, #98FF98)'
            }}
          />
        </div>
        <div className="w-8 h-8 rounded-full bg-white absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2" />
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getSignerStatus(recipient)} absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 border-2 border-black`}>
          {getSignerIcon(recipient.status)}
        </div>
      </div>
      {showArrow && idx < totalRecipients - 1 && (
        <div className="absolute z-30" style={{ right: '-48px', top: '50%', transform: 'translate(50%, -50%)' }}>
          <ChevronRight size={32} className="text-black" />
        </div>
      )}
      <div className="text-center" style={{ marginTop: '36px' }}>
        <p className="font-medium text-sm text-black truncate">{recipient.name}</p>
      </div>
    </div>
  );
};

return (
  <div className="relative h-4">
    <div className="absolute inset-0 flex top-0">
  {sortedRecipients.map((recipient, idx) => (
        <div key={`unit-${idx}`} className="flex-1 relative">
          <div className="w-full h-full flex items-center">
            <div className="w-full rounded-full" style={getSegmentStyle(idx)} />
          </div>

          <div className="absolute top-1/2 -translate-y-1/2 z-10" style={{ right: '-16px' }}>
            <div className="w-8 h-8 rounded-full bg-white" />
          </div>

          <div className="absolute top-1/2 -translate-y-1/2 z-20" style={{ right: '-16px' }}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              recipient.status === 'completed' ? 'bg-[#008455]' :
              (!recipient.routingOrder || recipient.routingOrder === 0) ? 'bg-[#98FF98]' :
              getSignerStatus(recipient)
            } border-2 border-black`}>
              {recipient.status === 'completed' ? <Check className="w-5 h-5 text-white" /> :
               (!recipient.routingOrder || recipient.routingOrder === 0) ? <Clock className="w-5 h-5 text-black" /> :
               getSignerIcon(recipient.status)}
            </div>
          </div>

          {idx < recipients.length - 1 && (
            <div className="absolute z-30" style={{ right: '-48px', top: '50%', transform: 'translate(50%, -50%)' }}>
              <ChevronRight size={32} className="text-black" />
            </div>
          )}
        </div>
      ))}
    </div>

    <div className="absolute w-full flex" style={{ top: '16px' }}>
  {sortedRecipients.map((recipient, idx) => (
    <div key={`label-${idx}`} className="flex-1 text-center">
      <p className="font-medium text-sm text-black truncate">{recipient.name}</p>
    </div>
  ))}
</div>
  </div>
);
};

// UserAccountMenu is now imported from ./UserAccountMenu

// Reassignment Modal Component
// MOTD Edit Modal Component
const MOTDEditModal = ({ isOpen, onClose, currentMotd, onSave, isSubmitting }) => {
  const [value, setValue] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen && currentMotd) {
      setValue(currentMotd.value || '');
      setIsActive(currentMotd.isActive !== false);
      setError(null);
    }
  }, [isOpen, currentMotd]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!value.trim()) {
      setError('Message cannot be empty');
      return;
    }

    try {
      await onSave(value.trim(), isActive);
    } catch (err) {
      setError(err.message || 'Failed to save message');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Edit Announcement</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={isSubmitting}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {currentMotd?.lastModifiedByName && (
              <div className="mb-4 p-3 bg-gray-50 rounded-md text-sm text-gray-600">
                Last modified by {currentMotd.lastModifiedByName}
                {currentMotd.lastModifiedAt && (
                  <> on {new Date(currentMotd.lastModifiedAt).toLocaleDateString()}</>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Announcement Message
                </label>
                <textarea
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Enter the announcement message..."
                  rows={5}
                  className={`w-full px-3 py-2 border rounded-md ${error ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  disabled={isSubmitting}
                />
                {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="motdActive"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="mr-2"
                  disabled={isSubmitting}
                />
                <label htmlFor="motdActive" className="text-sm text-gray-700">
                  Show this announcement to users
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  onClick={onClose}
                  className="flex-1 bg-gray-100 text-gray-700 hover:bg-gray-200"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#762dba] text-white hover:bg-[#3a1a6a]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

//2
const DocuSignDashboard = ({ currentView, onViewChange }) => {
  // Get all state and functions from the hook
  const {
    // State
    filteredEnvelopes,
    totalItems,
    searchEmails,
    searchType,
    statusFilters,
    dateFilter,
    customDateRange,
    currentPage,
    itemsPerPage,
    error,
    loading,
    isFetching,
    isPolling,
    hasSearched,
    expandedEnvelopes,
    expandedDetails,
    isPendingView,
    awaitingSignatureCount,
    isAuthLoading,
    userPhoto,
    userDisplayName,
    userEmail,
    isUserInfoLoading,
    signingInProgress,
    signedEnvelopeId,
    processingEnvelopeId,
    signingStatus,
    signingError,
    canReassign,
    reassignModalOpen,
    reassignEnvelopeId,
    reassignRecipient,
    isReassigning,
    reassignmentSuccess,
    sendingReminderKey,
    reminderSuccess,
    voidModalOpen,
    voidEnvelope,
    isVoiding,
    voidSuccess,
    removeDocModalOpen,
    removeDocEnvelopeId,
    removeDocTarget,
    isRemovingDoc,
    removeDocSuccess,
    envelopeDocuments,
    isLoadingDocuments,
    isLoadingDocView,
    viewingDocId,
    remindersUpdatingId,
    remindersSuccess,
    updatesAvailable,
    pollingState,
    motd,
    motdLoading,
    isMotdAdmin,
    motdModalOpen,
    isSavingMotd,

    // Setters
    setSearchType,
    setStatusFilters,
    setDateFilter,
    setCustomDateRange,
    setCurrentPage,
    setItemsPerPage,
    setExpandedEnvelopes,
    setUpdatesAvailable,
    setPollingState,
    setLastUpdateButtonClick,
    setReassignModalOpen,
    setReassignRecipient,
    setReassignEnvelopeId,
    setReassignmentSuccess,
    setReminderSuccess,
    setVoidModalOpen,
    setVoidSuccess,
    setRemoveDocModalOpen,
    setRemoveDocTarget,
    setRemoveDocEnvelopeId,
    setRemoveDocSuccess,
    setMotdModalOpen,
    setFilteredEnvelopes,
    setTotalItems,

    // Functions
    getAccessToken,
    fetchEnvelopes,
    handleSearch,
    handleSearchInput,
    resetSearch,
    showPendingEnvelopes,
    toggleEnvelopeView,
    handleSigningRequest,
    openReassignModal,
    handleReassign,
    handleSendReminder,
    openVoidModal,
    handleVoidEnvelope,
    fetchEnvelopeDocuments,
    handleViewDocument,
    handleSetReminders,
    openRemoveDocModal,
    handleRemoveDocument,
    searchTenantUsers,
    handleSaveMotd,
    toggleEnvelopeExpansion,
    formatDate,
    hasSequentialSigning,
    handleLogout,
    debouncedFetch,
    debug,
  } = useDashboard();

  // Track dashboard load once when userEmail is available
  useEffect(() => {
    if (userEmail) {
      trackDashboardLoad(userEmail, currentView);
    }
  }, [userEmail]); // Only fire when userEmail changes (typically once on auth)

  // Local function that uses userEmail from hook
  const needsUserSignature = (envelope) => {
    const isActive = ['sent', 'delivered'].includes(envelope.status?.toLowerCase());
    const isNotDeleted = !['deleted', 'voided'].includes(envelope.status?.toLowerCase());
    const needsSignature = envelope.recipients?.some(recipient => {
      const emailMatch = recipient.email?.toLowerCase() === userEmail?.toLowerCase();
      const statusMatch = ['sent', 'delivered'].includes(recipient.status?.toLowerCase());
      return emailMatch && statusMatch;
    });
    return isActive && isNotDeleted && needsSignature;
  };

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdowns = ['dateDropdown', 'statusDropdown'];
      dropdowns.forEach(id => {
        const dropdown = document.getElementById(id);
        const button = document.getElementById(`${id}Button`);
        if (dropdown && button && !dropdown.contains(event.target) && !button.contains(event.target)) {
          dropdown.style.display = 'none';
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

const renderEnvelope = (envelope) => {
  const isExpanded = expandedEnvelopes[envelope.envelopeId];
  const showDetails = expandedDetails[envelope.envelopeId];
  const completedCount = envelope.recipients?.filter(r => r.status === 'completed').length || 0;
  const totalCount = envelope.recipients?.length || 0;
  const requiresSignature = needsUserSignature(envelope);
  const isSequential = hasSequentialSigning(envelope);
const isTerminalState = ['declined', 'deleted', 'voided'].includes(envelope.status?.toLowerCase());

return (
<Card key={envelope.envelopeId} className={`overflow-hidden bg-white border-[1px] border-[#0e0e0f] relative ${
  processingEnvelopeId === envelope.envelopeId ? 'opacity-50' : ''
}`}>
  {isTerminalState && (
    <div className="absolute inset-0 flex items-center justify-center z-40">
      <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
        <defs>
          <linearGradient id="lineGradient" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#8B0000" />
            <stop offset="50%" stopColor="#FF0000" />
            <stop offset="100%" stopColor="#8B0000" />
          </linearGradient>
        </defs>
        <line x1="0" y1="0" x2="100%" y2="100%" stroke="url(#lineGradient)" strokeWidth="6" />
        <line x1="100%" y1="0" x2="0" y2="100%" stroke="url(#lineGradient)" strokeWidth="6" />
      </svg>
      <div className="bg-white px-3 z-50 text-red-500 font-medium">
        {envelope.status.charAt(0).toUpperCase() + envelope.status.slice(1)}
      </div>
    </div>
  )}
	  {processingEnvelopeId === envelope.envelopeId && (
		<div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
		  <span className="bg-white px-4 py-2 rounded-md text-sm font-medium shadow-lg">
			Processing signature...
		  </span>
		</div>
	  )}
    <CardContent className="pt-3 px-5 pb-1">
      {/* Top Row with Subject, Dates, Sign Button */}
      <div className="flex items-center justify-between -mb-1 relative">
		<span className="text-xs text-gray-600 text-center absolute w-full -top-1 z-10">
		  Status: {getStatusDisplay(envelope)}
		  {/* Silenced reminders must be visible without opening the envelope, or a
		      consent quietly stops chasing the patient and nobody notices. */}
		  {envelope.remindersEnabled === false && (
			<RemindersBadge remindersEnabled={envelope.remindersEnabled} className="ml-2 align-middle" />
		  )}
		</span>
  <div className="flex flex-1 items-center gap-4 min-w-0 -mt-7">
          <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center ${
            envelope.status === 'completed' ? 'bg-[#008455]' : 'bg-[#98FF98]'
          }`}>
            {envelope.status === 'completed' ?
              <Check className="w-3 h-3 text-white" /> :
              <Clock className="w-3 h-3 text-black" />
            }
          </div>
          <div className={`flex-1 min-w-0 ${requiresSignature ? 'max-w-[45%]' : 'max-w-[37%]'}`}>
            <h3 className="font-medium text-sm text-black truncate">{envelope.subject}</h3>
          </div>
        </div>

      <div className="flex items-center gap-2">
		 {requiresSignature && (
		  <Button
			  onClick={(e) => {
				e.stopPropagation();
				const recipient = envelope.recipients.find(r =>
				  r.email?.toLowerCase() === userEmail?.toLowerCase() &&
				  ['sent', 'delivered'].includes(r.status?.toLowerCase())
				);
				handleSigningRequest(envelope.envelopeId, e, recipient?.routingOrder || null);
			  }}
			  className="bg-[#762dba] text-white hover:bg-[#3a1a6a] absolute right-20 -top-2 w-24 h-16 p-0 flex flex-col items-center justify-center text-xs"
			  disabled={
				signingInProgress ||
				signedEnvelopeId === envelope.envelopeId ||
				processingEnvelopeId === envelope.envelopeId
			  }
			>
			{(signingInProgress || signedEnvelopeId === envelope.envelopeId || processingEnvelopeId === envelope.envelopeId) ? (
			  <span className="text-sm">Signing...</span>
			) : (
			  <span className="text-sm">Sign</span>
			)}
		  </Button>
		)}
         <button
  onClick={() => toggleEnvelopeExpansion(envelope.envelopeId)}
  className="hover:bg-gray-100 rounded translate-y-2 -translate-x-5"
>
  <div style={{ transform: 'scale(2)' }}>
    {isExpanded ?
      <ChevronUp className="w-7 h-7 text-black" /> :
      <ChevronDown className="w-7 h-7 text-black" />
    }
  </div>
</button>
          </div>
        </div>

{/* Progress Section */}
<div className="relative pb-4 w-[80%]">
  {isSequential ? (
    <SequentialProgress
      recipients={envelope.recipients}
      formatDate={formatDate}
    />
  ) : (
    <div className="flex justify-between items-center h-4">
      {[...envelope.recipients]
        .sort((a, b) => {
  if (hasSequentialSigning(envelope)) {
    const orderA = a.routingOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.routingOrder ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  }
  return 0; // Keep original order for non-sequential
})
        .map((recipient, idx) => (
          <div key={`${recipient.email}-${idx}`} className="flex-1 relative">
            <div className="w-full h-full flex items-center justify-end pl-3">
        <div className="w-full">
          <div className={`h-3 rounded-full ${idx === 0 ? 'bg-[#767779]' : 'bg-white'}`}>
            <div
              className="h-3 transition-all duration-500 rounded-full ml-auto"
              style={{
                width: idx > 0 ? '90%' : '100%',
                background: recipient.status === 'completed'
                  ? '#008455'
                  : 'linear-gradient(to right, #008455, #98FF98)'
              }}
            />
          </div>
        </div>
      </div>

      <div className="absolute top-1/2 -translate-y-1/2 z-10" style={{ right: '-16px' }}>
        <div className="w-8 h-8 rounded-full bg-white" />
      </div>

      <div className="absolute top-1/2 -translate-y-1/2 z-20" style={{ right: '-16px' }}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${getSignerStatus(recipient)} border-2 border-black`}>
          {getSignerIcon(recipient.status)}
        </div>
      </div>
    </div>
  ))}

<div className="absolute w-full flex" style={{ top: '16px' }}>
  {[...envelope.recipients]
    .sort((a, b) => {
      const orderA = a.routingOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.routingOrder ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    })
    .map((recipient, idx) => (
          <div key={`label-${idx}`} className="flex-1 text-center">
            <p className="font-medium text-sm text-black truncate">{recipient.name}</p>
          </div>
        ))}
      </div>
    </div>
  )}
</div>

{isExpanded && (
          <div className="mt-4">
            <div className="flex justify-between">
              {[...envelope.recipients]
                .sort((a, b) => {
                  const orderA = a.routingOrder ?? Number.MAX_SAFE_INTEGER;
                  const orderB = b.routingOrder ?? Number.MAX_SAFE_INTEGER;
                  return orderA - orderB;
                })
                .map((recipient, idx) => (
                <div key={`detail-${recipient.email}-${idx}`} className="flex-1 text-center">
                  <p className="text-xs text-gray-600">{recipient.email}</p>
                  {recipient.events?.sort((a, b) => new Date(b.eventDateTime) - new Date(a.eventDateTime)).map((event, eventIdx) => (
                    <p key={eventIdx} className="text-xs text-gray-600">
                      {event.status} on {formatDate(event.eventDateTime)}
                    </p>
                  ))}
                  {!recipient.events?.length && (
  <p className="text-xs text-gray-600">
    {recipient.status === 'completed' && (
      <>Signed on {formatDate(recipient.timestamps?.signedDateTime).split(' ').slice(0,1)}<br/>{formatDate(recipient.timestamps?.signedDateTime).split(' ').slice(1).join(' ')}</>
    )}
    {recipient.status === 'delivered' && (
      <>Received on {formatDate(recipient.timestamps?.deliveredDateTime).split(' ').slice(0,1)}<br/>{formatDate(recipient.timestamps?.deliveredDateTime).split(' ').slice(1).join(' ')}</>
    )}
    {recipient.status === 'sent' && (
      <>Requested {formatDate(recipient.timestamps?.sentDateTime || recipient.reassignedAt || envelope.timestamps?.sentDateTime || recipient.sentDateTime || envelope.timestamps?.createdDateTime).split(' ').slice(0,1)}<br/>{formatDate(recipient.timestamps?.sentDateTime || recipient.reassignedAt || envelope.timestamps?.sentDateTime || recipient.sentDateTime || envelope.timestamps?.createdDateTime).split(' ').slice(1).join(' ')}</>
    )}
    {recipient.status === 'created' && 'Not yet sent'}
  </p>
)}
                  {['autoresponded', 'declined'].includes(recipient.status?.toLowerCase()) && (
                    <p className="text-xs text-red-600 font-medium">
                      {recipient.status?.toLowerCase() === 'declined'
                        ? 'Declined by recipient'
                        : 'Email could not be delivered - check the address'}
                    </p>
                  )}
                  {/* Action buttons */}
                  <div className="mt-2 flex items-center gap-1">
                    {/* Remind button - show for sent/delivered recipients */}
                    {['sent', 'delivered'].includes(recipient.status?.toLowerCase()) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSendReminder(envelope.envelopeId, recipient.docuSignRecipientId, recipient.name);
                        }}
                        disabled={sendingReminderKey === `${envelope.envelopeId}:${recipient.docuSignRecipientId}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded hover:bg-amber-200 transition-colors disabled:opacity-50"
                        title="Send signing reminder email"
                      >
                        <Bell className="w-3 h-3" />
                        {sendingReminderKey === `${envelope.envelopeId}:${recipient.docuSignRecipientId}` ? 'Sending...' : 'Remind'}
                      </button>
                    )}
                    {/* Reassign button - show for any non-completed recipient when user has permission */}
                    {canReassign && recipient.status?.toLowerCase() !== 'completed' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openReassignModal(envelope, recipient);
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors"
                        title="Reassign this recipient to someone else"
                      >
                        <UserCog className="w-3 h-3" />
                        Reassign
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/* Documents section */}
            {envelopeDocuments[envelope.envelopeId] && envelopeDocuments[envelope.envelopeId].length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs font-medium text-gray-600 mb-1">Documents</p>
                <div className="flex flex-wrap gap-2">
                  {envelopeDocuments[envelope.envelopeId].map((doc) => (
                    <div key={doc.documentId} className="flex items-center gap-1 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                      <FileText className="w-3 h-3 text-gray-400" />
                      <span>{doc.name}</span>
                      {/* Non-destructive read/print path. Always available - viewing never
                          changes the envelope, and it must be easier to reach than Reassign. */}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleViewDocument(envelope.envelopeId, doc.documentId); }}
                        disabled={viewingDocId === doc.documentId}
                        className="ml-1 text-sky-600 hover:text-sky-800 disabled:opacity-50"
                        title="Open this document to read or print it. Does not change the envelope."
                      >
                        <Eye className="w-3 h-3" />
                      </button>
                      {!isTerminalState && envelope.status?.toLowerCase() !== 'completed' && envelopeDocuments[envelope.envelopeId].length > 1 && !envelope.recipients?.some(r => r.status === 'completed') && (
                        <button
                          onClick={(e) => { e.stopPropagation(); openRemoveDocModal(envelope.envelopeId, doc); }}
                          className="ml-1 text-red-500 hover:text-red-700"
                          title="Remove this document"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!envelopeDocuments[envelope.envelopeId] && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <button
                  onClick={(e) => { e.stopPropagation(); fetchEnvelopeDocuments(envelope.envelopeId); }}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Show documents...
                </button>
              </div>
            )}
				<div className="flex justify-between text-xs text-gray-600 mt-3">
				  <div className="flex items-center gap-4">
					<span>Created: {formatDate(envelope.timestamps?.createdDateTime)}</span>
					<span>Updated: {formatDate(envelope.timestamps?.lastModifiedDateTime)}</span>
					<span>Expires: {formatDate(envelope.timestamps?.expireDateTime)}</span>
					{/* Reminder toggle sits BEFORE Void deliberately - staff arrive wanting the
					    emails to stop, and Void is what they reach for when nothing else exists. */}
					{!isTerminalState && envelope.status?.toLowerCase() !== 'completed' && (
					  <button
						onClick={(e) => {
						  e.stopPropagation();
						  handleSetReminders(envelope.envelopeId, envelope.remindersEnabled === false);
						}}
						disabled={remindersUpdatingId === envelope.envelopeId}
						className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors disabled:opacity-50 ${
						  envelope.remindersEnabled === false
							? 'text-slate-700 bg-slate-100 hover:bg-slate-200'
							: 'text-sky-700 bg-sky-50 hover:bg-sky-100'
						}`}
						title={envelope.remindersEnabled === false
						  ? 'Resume automatic reminder emails for this envelope'
						  : 'Stop automatic reminder emails. Does not void the envelope - it stays open and can still be signed.'}
					  >
						{envelope.remindersEnabled === false
						  ? <><Bell className="w-3 h-3" />{remindersUpdatingId === envelope.envelopeId ? 'Working...' : 'Resume reminders'}</>
						  : <><BellOff className="w-3 h-3" />{remindersUpdatingId === envelope.envelopeId ? 'Working...' : 'Stop reminders'}</>}
					  </button>
					)}
					{!isTerminalState && envelope.status?.toLowerCase() !== 'completed' && (
					  <button
						onClick={(e) => {
						  e.stopPropagation();
						  openVoidModal(envelope);
						}}
						className="inline-flex items-center gap-1 px-2 py-1 text-xs text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors"
						title="Void this envelope"
					  >
						<Ban className="w-3 h-3" />
						Void
					  </button>
					)}
				  </div>
				  <span>Envelope ID: {envelope.envelopeId}</span>
				</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const toggleEnvelopeViewLocal = async () => {
  debug('Toggle clicked. Current isPendingView:', isPendingView);

  // Clear any existing updates banner when switching views
  setUpdatesAvailable(false);

  if (isPendingView) {
    debug('Was pending, clearing to normal view');
    await toggleEnvelopeView();
  } else {
    debug('Was normal, switching to pending view');
    await showPendingEnvelopes();
  }

  debug('Toggle complete. New isPendingView will be:', !isPendingView);
};

//5
// Main return statement
return (
  <ProtectedComponent>
    {(loading || isUserInfoLoading || !userDisplayName) ? (
      <div className="p-4 max-w-6xl mx-auto">
        <div className="bg-[#2db3e1] rounded-lg">
          <div className="p-6 text-center text-white">
            Loading your dashboard...
          </div>
        </div>
      </div>
    ) : (
    <div className="p-4 max-w-6xl mx-auto">
      <div style={{ backgroundColor: '#2db3e1' }} className="rounded-lg">
        <div className="p-6">
          {/* Welcome message */}
          <div className="flex justify-between items-start mb-4">
            <div>
             {!isUserInfoLoading && userDisplayName && (
              <div className="flex items-center gap-4 text-white">
                <p className="text-white text-2xl">
                  Welcome {userDisplayName}, {
                    isAuthLoading ?
                    "checking your pending envelopes..." :
                    `you have ${awaitingSignatureCount} envelopes awaiting your signature`
                  }
                </p>
                  {awaitingSignatureCount > 0 && (
                    <Button
                      onClick={toggleEnvelopeView}
                      className={`${
                        isPendingView
                          ? 'bg-[#762dba] text-white hover:bg-[#3a1a6a]'
                          : 'bg-white text-black hover:bg-gray-100'
                      } ml-2 font-normal flex items-center gap-2`}
                      disabled={loading}
                    >
                      {isPendingView ? 'View all envelopes' : 'Show pending envelopes'}
                      {isPendingView && <Check className="w-4 h-4" />}
                    </Button>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <UserAccountMenu
                userDisplayName={userDisplayName}
                userEmail={userEmail}
                userPhoto={userPhoto}
                handleLogout={handleLogout}
                currentView={currentView}
                onViewChange={onViewChange}
              />
            </div>
          </div>

		  {/* Announcement / MOTD */}
		  {(motdLoading || (motd?.isActive !== false)) && (
			<div className="text-white mb-4">
			  {motdLoading ? (
				<p className="text-sm opacity-75">Loading announcement...</p>
			  ) : motd?.value ? (
				<div className="flex items-start gap-2">
				  <p className="text-sm opacity-90 flex-1 whitespace-pre-wrap">{motd.value}</p>
				  {isMotdAdmin && (
					<button
					  onClick={() => setMotdModalOpen(true)}
					  className="p-1 rounded hover:bg-white/20 transition-colors flex-shrink-0"
					  title="Edit announcement"
					>
					  <Pencil className="w-4 h-4" />
					</button>
				  )}
				</div>
			  ) : (
				<p className="text-sm opacity-75">
				  To suggest a feature or report a bug, please email david.wood@springfertility.com, or message me on Teams.
				</p>
			  )}
			</div>
		  )}

 {/* Search section */}
          <div className="flex items-center gap-4 mb-6">
            <SearchTypeSelector
              searchType={searchType}
              setSearchType={setSearchType}
              disabled={loading || (isFetching && !isPolling)}
            />
            <div className="flex-1">
              <Input
                type="text"
                placeholder={
                  searchType === 'email' ? "Search by email" :
                  searchType === 'subject' ? "Search by subject" :
                  "Search by envelope ID"
                }
                value={searchEmails[0]}
                onChange={(e) => handleSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                className="w-full"
                disabled={loading || (isFetching && !isPolling)}
              />
            </div>
            <Button
			  onClick={handleSearch}
			  className="bg-white text-black font-normal hover:bg-gray-100 min-w-[100px]"
			  disabled={loading || (isFetching && !isPolling)}
			>
			  {(isFetching && !isPolling) ? (
				<RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
			  ) : (
				<Search className="w-4 h-4 mr-2" />
			  )}
			  {(isFetching && !isPolling) ? 'Searching...' : 'Search'}
			</Button>
           </div>

          {/* Filter row */}
          <div className="flex items-center gap-4">
            {/* Date Filter */}
            <div className="relative">
			<Button
			  id="dateDropdownButton"
			  onClick={() => {
				if (loading || (isFetching && !isPolling)) return;
				const dateDropdown = document.getElementById('dateDropdown');
				const statusDropdown = document.getElementById('statusDropdown');
				if (dateDropdown) {
				  const newDisplay = dateDropdown.style.display === 'none' ? 'block' : 'none';
				  dateDropdown.style.display = newDisplay;
				  if (newDisplay === 'block' && statusDropdown) {
					statusDropdown.style.display = 'none';
				  }
				}
			  }}
			  className="bg-white text-black font-normal hover:bg-gray-100 inline-flex items-center h-10 px-4 py-2 w-40"
			  disabled={loading || (isFetching && !isPolling)}
			>
                <div className="flex-1 text-left">
                  {dateFilter === 'custom' ? 'Custom Date Range' :
                   dateFilter.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </div>
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
              <div
                id="dateDropdown"
                className="absolute z-50 mt-1 w-64 bg-white rounded-md shadow-lg"
                style={{ display: 'none' }}
              >
                <div className="p-4">
                  {[
                    ['all-time', 'All time'],
                    ['last-12-months', 'Last 12 months'],
                    ['last-6-months', 'Last 6 months'],
                    ['last-30-days', 'Last 30 days'],
                    ['last-week', 'Last week'],
                    ['last-24-hours', 'Last 24 hours'],
                    ['custom', 'Custom']
                  ].map(([value, label]) => (
                    <div key={value} className="mb-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="dateFilter"
                          value={value}
                          checked={dateFilter === value}
						onChange={async (e) => {
						  if (isFetching && !isPolling) return;
						  setDateFilter(e.target.value);
						  if (hasSearched) {
							await debouncedFetch(() => fetchEnvelopes(true));
						  }
						}}
                          className="mr-2"
                          disabled={loading}
                        />
                        {label}
                      </label>
                    </div>
                  ))}

                  {dateFilter === 'custom' && (
                    <div className="mt-4 space-y-4">
                      <div>
                        <label className="block text-sm mb-1">From</label>
                        <Input
                          type="date"
                          value={customDateRange.from}
                          onChange={async (e) => {
                            setCustomDateRange(prev => ({
                              ...prev,
                              from: e.target.value
                            }));
                            if (hasSearched && customDateRange.to) {
                              await handleSearch();
                            }
                          }}
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm mb-1">To</label>
                        <Input
                          type="date"
                          value={customDateRange.to}
                          onChange={async (e) => {
                            setCustomDateRange(prev => ({
                              ...prev,
                              to: e.target.value
                            }));
                            if (hasSearched && customDateRange.from) {
                              await handleSearch();
                            }
                          }}
                          disabled={loading}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Status Filter */}
            <div className="relative">
			 <Button
			  id="statusDropdownButton"
			  onClick={() => {
				if (loading || (isFetching && !isPolling)) return;
				const statusDropdown = document.getElementById('statusDropdown');
				const dateDropdown = document.getElementById('dateDropdown');
				if (statusDropdown) {
				  // Toggle this dropdown
				  statusDropdown.style.display = statusDropdown.style.display === 'none' ? 'block' : 'none';
				  // Always hide the other dropdown
				  if (dateDropdown) {
					dateDropdown.style.display = 'none';
				  }
				}
			  }}
			  className="bg-white text-black font-normal hover:bg-gray-100 inline-flex items-center justify-between h-10 px-4 py-2 w-40"
			  disabled={loading || (isFetching && !isPolling)}
			>
			  <span className="flex-1 text-center">
				{statusFilters[0]?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'Status'}
			  </span>
			  <ChevronDown className="h-4 w-4 ml-2 flex-shrink-0" />
			</Button>
			<div
			  id="statusDropdown"
			  className="absolute z-50 mt-1 w-48 bg-white rounded-md shadow-lg"
			  style={{ display: 'none' }}
			>
			  <div className="p-4">
				{[
				  ['all', 'All statuses'],
				  ['created', 'Created (Draft)'],
				  ['in-progress', 'In Progress'],
				  ['completed', 'Completed'],
				  ['declined', 'Declined'],
				  ['deleted', 'Deleted'],
				  ['voided', 'Voided']
				].map(([value, label]) => (
				  <div key={value} className="mb-2">
					<label className="flex items-center">
					  <input
						type="radio"
						name="statusFilter"
						value={value}
						checked={statusFilters.includes(value)}
						onChange={(e) => {
						  setStatusFilters([e.target.value]);
						  // Hide dropdown after selection
						  const statusDropdown = document.getElementById('statusDropdown');
						  if (statusDropdown) {
							statusDropdown.style.display = 'none';
						  }
						}}
						className="mr-2"
						disabled={loading || (isFetching && !isPolling)}
					  />
					  {label}
					</label>
				  </div>
				))}
			  </div>
			</div>
            </div>
	{/* Results per page */}
				 <select
				  className="border rounded-md h-10 px-4 py-2 w-40 bg-white text-black font-normal hover:bg-gray-100 appearance-none cursor-pointer"
				  value={itemsPerPage}
				  onChange={async (e) => {
					if (isFetching && !isPolling) return;
					const newValue = Number(e.target.value);
					setItemsPerPage(newValue);

					// Create a custom fetch function that uses the new value directly
					const fetchWithNewSize = async () => {
					  const params = new URLSearchParams();
					  params.append('pageSize', newValue.toString());
					  params.append('page', '1');

					  if (!statusFilters.includes('all')) {
						const apiStatuses = statusFilters.flatMap(status => {
						  if (status === 'in-progress') return ['sent', 'delivered'];
						  return [status];
						});
						params.append('status', apiStatuses.join(','));
					  }

					  params.append('dateFilter', dateFilter);
					  if (dateFilter === 'custom') {
						if (customDateRange.from) params.append('fromDate', customDateRange.from);
						if (customDateRange.to) params.append('toDate', customDateRange.to);
					  }

					  searchEmails.forEach(term => {
						const trimmedTerm = term.trim();
						if (trimmedTerm) {
						  if (searchType === 'email') {
							params.append('searchEmails', trimmedTerm.toLowerCase());
						  } else if (searchType === 'subject') {
							params.append('subject', trimmedTerm);
						  } else if (searchType === 'envelopeId') {
							params.append('envelopeId', trimmedTerm);
						  }
						}
					  });

					  const token = await getAccessToken();
					  const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/envelopes?${params.toString()}`, {
						headers: {
						  'Accept': 'application/json',
						  'Authorization': `Bearer ${token}`,
						  'Cache-Control': 'no-cache'
						}
					  });

					  if (!response.ok) {
						throw new Error('Failed to fetch envelopes');
					  }

					  const data = await response.json();
					  setFilteredEnvelopes(data.envelopes);
					  setTotalItems(data.totalCount);
					  setCurrentPage(1);
					};

					await fetchWithNewSize();
				  }}
				  disabled={loading || (isFetching && !isPolling)}
			  style={{
				backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E")`,
				backgroundRepeat: 'no-repeat',
				backgroundPosition: 'right 1rem center',
				backgroundSize: '1rem'
			  }}
			>
			  <option value={10}>10 per page</option>
			  <option value={25}>25 per page</option>
			  <option value={50}>50 per page</option>
			</select>

			<Button
			  onClick={resetSearch}
			  className="bg-white text-black font-normal hover:bg-gray-100"
			  disabled={loading}
			>
			  Reset
			</Button>


          </div>

          {/* Error messages - Consolidated error handling */}
			{(error || signingError) && (
			  <div className="mt-4 p-3 bg-red-500 text-white rounded">
				{error || signingError}
			  </div>
			)}

          {/* Reassignment success message */}
          {reassignmentSuccess && (
            <div className="mt-4 p-3 bg-green-500 text-white rounded flex items-center justify-between">
              <span>{reassignmentSuccess.message}</span>
              <button
                onClick={() => setReassignmentSuccess(null)}
                className="ml-4 hover:bg-green-600 rounded p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Reminder success message */}
          {reminderSuccess && (
            <div className="mt-4 p-3 bg-amber-500 text-white rounded flex items-center justify-between">
              <span>{reminderSuccess.message}</span>
              <button
                onClick={() => setReminderSuccess(null)}
                className="ml-4 hover:bg-amber-600 rounded p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Void success message */}
          {voidSuccess && (
            <div className="mt-4 p-3 bg-red-500 text-white rounded flex items-center justify-between">
              <span>{voidSuccess.message}</span>
              <button
                onClick={() => setVoidSuccess(null)}
                className="ml-4 hover:bg-red-600 rounded p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Document removal success message */}
          {removeDocSuccess && (
            <div className="mt-4 p-3 bg-amber-500 text-white rounded flex items-center justify-between">
              <span>{removeDocSuccess.message}</span>
              <button
                onClick={() => setRemoveDocSuccess(null)}
                className="ml-4 hover:bg-amber-600 rounded p-1"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

{/* Results section */}
          <div className="mt-4">
            {loading ? (
              <div className="text-center py-4 text-white">
                Loading...
              </div>
            ) : (isFetching && !isPolling) ? (
              <div className="text-center py-4 text-white">
                Searching for envelopes...
              </div>
            ) : !hasSearched ? (
              <div className="text-center py-4 text-white">
                {loading ? "Loading your envelopes..." : "Enter search criteria and click Search to view envelopes"}
              </div>
            ) : filteredEnvelopes.length > 0 ? (
              <>
<div className="mb-4 flex items-center justify-end">
 <div className="text-white text-2xl">
    Showing {((currentPage - 1) * (isPendingView ? 50 : itemsPerPage)) + 1} to {Math.min(currentPage * (isPendingView ? 50 : itemsPerPage), totalItems)} of {totalItems} results
  </div>
  {debug('Update button render check:', {
    updatesAvailable,
    isPendingView,
    filteredEnvelopes: filteredEnvelopes.length
  })}
  {updatesAvailable && (
	<Button
	  onClick={async () => {
		const now = Date.now();
		setLastUpdateButtonClick(now);
		setPollingState(prev => ({
		  ...prev,
		  cooldownUntil: now + 60000 // Only set cooldown after clicking the button
		}));

		localStorage.removeItem('processingEnvelopes');

		try {
		  if (isPendingView) {
			await showPendingEnvelopes();
		  } else {
			await fetchEnvelopes(false);
		  }
		  // Only set updatesAvailable to false after successful refresh
		  setUpdatesAvailable(false);
		} catch (error) {
		  console.error('Error refreshing envelopes:', error);
		  // Keep updates available if refresh failed
		}
	  }}
	  className="bg-white text-black font-normal hover:bg-gray-100 flex items-center gap-2 absolute left-1/2 -translate-x-1/2 pulse-animation"
	>
	  <RefreshCcw className="w-4 h-4" />
	  New updates available
	</Button>
  )}
</div>

			 <div className="space-y-2">
			{filteredEnvelopes.map((envelope) => renderEnvelope(envelope))}
			</div>

                {totalItems > (isPendingView ? 50 : itemsPerPage) && (
				<div className="flex justify-center gap-4 mt-3">
                    <Button
				 onClick={async () => {
				  if (isFetching && !isPolling) return;
				  const newPage = Math.max(1, currentPage - 1);
				  setCurrentPage(newPage);
				  await debouncedFetch(() => fetchEnvelopes(false, false, newPage));
				}}
                      disabled={currentPage === 1 || loading}
                      className="bg-white text-black font-normal hover:bg-gray-100"
                    >
                      Previous
                    </Button>
                    <span className="flex items-center text-white">
                      Page {currentPage} of {Math.ceil(totalItems / itemsPerPage)}
                    </span>
				<Button
				onClick={async () => {
				  if (isFetching && !isPolling) return;
				  const newPage = currentPage + 1;
				  setCurrentPage(newPage);
				  await debouncedFetch(() => fetchEnvelopes(false, false, newPage));
				}}
				  disabled={currentPage >= Math.ceil(totalItems / itemsPerPage) || loading}
				  className="bg-white text-black font-normal hover:bg-gray-100"
				>
				  Next
				</Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4 text-white">
                No documents found
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
	)}

      {/* Reassignment Modal */}
      <ReassignmentModal
        isOpen={reassignModalOpen}
        onClose={() => {
          setReassignModalOpen(false);
          setReassignRecipient(null);
          setReassignEnvelopeId(null);
        }}
        recipient={reassignRecipient}
        envelopeId={reassignEnvelopeId}
        onReassign={(data) => handleReassign(data.newEmail, data.newName, data.justification)}
        isSubmitting={isReassigning}
        searchUsers={searchTenantUsers}
        onViewDocuments={reassignEnvelopeId ? () => handleViewDocument(reassignEnvelopeId, 'combined') : undefined}
      />

      {/* Void Envelope Modal */}
      <VoidEnvelopeModal
        isOpen={voidModalOpen}
        onClose={() => { setVoidModalOpen(false); }}
        envelope={voidEnvelope}
        onVoid={handleVoidEnvelope}
        isSubmitting={isVoiding}
      />
      <RemoveDocumentModal
        isOpen={removeDocModalOpen}
        onClose={() => { setRemoveDocModalOpen(false); setRemoveDocTarget(null); setRemoveDocEnvelopeId(null); }}
        document={removeDocTarget}
        envelopeSubject={removeDocEnvelopeId ? filteredEnvelopes.find(e => e.envelopeId === removeDocEnvelopeId)?.subject : null}
        onRemove={handleRemoveDocument}
        isSubmitting={isRemovingDoc}
      />

      {/* MOTD Edit Modal */}
      <MOTDEditModal
        isOpen={motdModalOpen}
        onClose={() => setMotdModalOpen(false)}
        currentMotd={motd}
        onSave={handleSaveMotd}
        isSubmitting={isSavingMotd}
      />
  </ProtectedComponent>
)
};

export default DocuSignDashboard;
